import type { OrderDetailResponse, OrderListItem, OrderStatus } from "../types/api.js";
import { apiRequest } from "./api-client.js";

function withQuery(path: string, params: Record<string, string | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") q.set(k, v);
  }
  const s = q.toString();
  return s ? `${path}?${s}` : path;
}

export async function fetchOrders(
  token: string,
  params: { tenantId?: string; status?: OrderStatus; orderNumber?: string; customerReference?: string } = {},
): Promise<{ orders: OrderListItem[] }> {
  return apiRequest<{ orders: OrderListItem[] }>(withQuery("/api/v1/orders", params), { method: "GET", token });
}

/** Alias para admin detail page */
export async function fetchOrdersForTenant(
  token: string,
  params: { tenantId: string },
): Promise<{ orders: OrderListItem[] }> {
  return fetchOrders(token, params);
}

export async function fetchOrderDetail(token: string, orderId: string): Promise<OrderDetailResponse> {
  return apiRequest<OrderDetailResponse>(`/api/v1/orders/${orderId}`, { method: "GET", token });
}

export async function createOrder(
  token: string,
  body: {
    tenantId?: string;
    orderNumber?: string;
    customerReference?: string | null;
    status?: OrderStatus;
    notes?: string | null;
    requestedDate?: string | null;
    lines: Array<{ productId: string; quantity: number; unit?: string | null }>;
  },
): Promise<OrderDetailResponse> {
  return apiRequest<OrderDetailResponse>("/api/v1/orders", { method: "POST", token, body });
}

export async function patchOrderStatus(
  token: string,
  orderId: string,
  status: OrderStatus,
): Promise<{ order: OrderDetailResponse["order"] }> {
  return apiRequest<{ order: OrderDetailResponse["order"] }>(`/api/v1/orders/${orderId}/status`, {
    method: "PATCH",
    token,
    body: { status },
  });
}

/** Fase 6.1 — registrar cantidad picada en una línea. */
export async function pickOrderLine(
  token: string,
  orderId: string,
  lineId: string,
  quantity: number,
): Promise<OrderDetailResponse> {
  return apiRequest<OrderDetailResponse>(`/api/v1/orders/${orderId}/lines/${lineId}/pick`, {
    method: "POST",
    token,
    body: { quantity },
  });
}

/** Fase 6.2 — empaquetar pedido (todas líneas picadas + reservas activas). */
export async function packOrder(token: string, orderId: string): Promise<OrderDetailResponse> {
  return apiRequest<OrderDetailResponse>(`/api/v1/orders/${orderId}/pack`, { method: "POST", token, body: {} });
}

/** Fase 6.3 / 6.6 — expedición. */
export async function shipOrder(
  token: string,
  orderId: string,
  body: {
    carrierName?: string | null;
    carrierId?: string | null;
    carrierCode?: string | null;
    serviceLevel?: string | null;
    externalReference?: string | null;
    shipmentNumber?: string | null;
    trackingReference?: string | null;
    dispatchReference?: string | null;
    shippingNotes?: string | null;
    shippedAt?: string | null;
  },
): Promise<OrderDetailResponse> {
  return apiRequest<OrderDetailResponse>(`/api/v1/orders/${orderId}/ship`, { method: "POST", token, body });
}

/** Fase 6.4 — entrega final. */
export async function deliverOrder(
  token: string,
  orderId: string,
  body: {
    deliveredAt?: string | null;
    deliveryReference?: string | null;
    receiverName?: string | null;
    deliveryNotes?: string | null;
    statusReason?: string | null;
  },
): Promise<OrderDetailResponse> {
  return apiRequest<OrderDetailResponse>(`/api/v1/orders/${orderId}/deliver`, { method: "POST", token, body });
}
